import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout, FilterBar, LeadCard, Pagination, PaginationInfo } from '../components';
import type { FilterState } from '../components/FilterBar';
import type { LeadListItem, Pagination as PaginationType, User } from '../types';
import { leadsApi, authApi } from '../utils/api';

export function Dashboard() {
  const [user, setUser] = useState<User | null>(null);
  const [leads, setLeads] = useState<LeadListItem[]>([]);
  const [pagination, setPagination] = useState<PaginationType>({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [filters, setFilters] = useState<FilterState>({
    status: '',
    prop_type: '',
    search: '',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Check authentication
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const stored = localStorage.getItem('rewagent_user');
        if (stored) {
          setUser(JSON.parse(stored));
        } else {
          navigate('/');
        }
      } catch {
        navigate('/');
      }
    };
    checkAuth();
  }, [navigate]);

  // Fetch leads
  const fetchLeads = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    setError(null);

    try {
      const data = await leadsApi.list({
        page: pagination.page,
        limit: pagination.limit,
        status: filters.status || undefined,
        prop_type: filters.prop_type || undefined,
        search: filters.search || undefined,
      });
      
      setLeads(data.leads || []);
      setPagination(data.pagination || pagination);
    } catch (err) {
      if (err instanceof Error && err.message.includes('401')) {
        localStorage.removeItem('rewagent_user');
        navigate('/');
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [user, pagination.page, pagination.limit, filters, navigate]);

  useEffect(() => {
    if (user) {
      fetchLeads();
    }
  }, [user, fetchLeads]);

  const handleFilterChange = (newFilters: FilterState) => {
    setFilters(newFilters);
    setPagination((p) => ({ ...p, page: 1 })); // Reset to first page on filter change
  };

  const handlePageChange = (page: number) => {
    setPagination((p) => ({ ...p, page }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch (err) {
      console.error('Logout error:', err);
    }
    localStorage.removeItem('rewagent_user');
    localStorage.removeItem('rewagent_token');
    navigate('/');
  };

  return (
    <Layout showHeader userName={user?.name} onLogout={handleLogout}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Leads</h1>
          <p className="text-gray-600 mt-1">
            {user?.client_name ? `${user.client_name} - ` : ''}Manage and track your leads
          </p>
        </div>

        {/* Filters */}
        <div className="mb-6">
          <FilterBar onFilter={handleFilterChange} initialValues={filters} />
        </div>

        {/* Error state */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-red-700">{error}</span>
            </div>
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <svg className="animate-spin h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </div>
        )}

        {/* Empty state */}
        {!loading && leads.length === 0 && (
          <div className="text-center py-12">
            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-1">No leads found</h3>
            <p className="text-gray-500">
              {filters.status || filters.prop_type || filters.search
                ? 'Try adjusting your filters'
                : 'Leads will appear here once they are added'}
            </p>
          </div>
        )}

        {/* Leads grid */}
        {!loading && leads.length > 0 && (
          <>
            <div className="mb-4">
              <PaginationInfo pagination={pagination} />
            </div>
            
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {leads.map((lead) => (
                <LeadCard key={lead.id} lead={lead} />
              ))}
            </div>

            <div className="mt-6">
              <Pagination pagination={pagination} onPageChange={handlePageChange} />
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
